import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { PageHeader, StatusPill } from "@/components/admin/PageHeader";
import { RequirePermission } from "@/components/admin/RequirePermission";
import {
  deleteSocialPostFn,
  duplicateSocialPostFn,
  cancelSocialPostFn,
  listSocialPostsFn,
} from "@/lib/social/social.functions";
import { POST_STATUSES, platformLabel, type SocialPost } from "@/lib/social/types";

export const Route = createFileRoute("/admin/social/")({
  head: () => ({
    meta: [
      { title: "Social Media Studio — CB16 Console" },
      { name: "description", content: "Draft, schedule and publish CB16 posts to Facebook and Instagram." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: () => (
    <RequirePermission perm="campaigns.read">
      <SocialStudioPage />
    </RequirePermission>
  ),
});

const card = "rounded-sm border border-black/10 bg-white p-5";
const btn =
  "inline-flex items-center justify-center border border-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-black transition hover:border-[#C6A15B] hover:text-[#C6A15B] disabled:cursor-wait disabled:opacity-50";
const btnDark =
  "inline-flex items-center justify-center border border-black bg-black px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white transition hover:border-[#C6A15B] hover:bg-[#C6A15B]";
const input =
  "border border-black/15 bg-white px-3 py-2 text-sm text-black focus:border-[#C6A15B] focus:outline-none";

function tone(status: string) {
  if (status === "published") return "success" as const;
  if (status === "scheduled" || status === "publishing") return "info" as const;
  if (status === "failed") return "danger" as const;
  if (status === "cancelled") return "warn" as const;
  return "neutral" as const;
}

function when(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function SocialStudioPage() {
  const navigate = useNavigate();
  const listFn = useServerFn(listSocialPostsFn);
  const dupFn = useServerFn(duplicateSocialPostFn);
  const delFn = useServerFn(deleteSocialPostFn);
  const cancelFn = useServerFn(cancelSocialPostFn);

  const [posts, setPosts] = useState<SocialPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      setPosts((await listFn({ data: {} })) as SocialPost[]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load posts.");
    } finally {
      setLoading(false);
    }
  }, [listFn]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return posts.filter(
      (p) =>
        (!status || p.status === status) &&
        (!q || (p.title + " " + p.caption).toLowerCase().includes(q)),
    );
  }, [posts, status, search]);

  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    for (const p of posts) map[p.status] = (map[p.status] ?? 0) + 1;
    return map;
  }, [posts]);

  async function act(key: string, fn: () => Promise<unknown>) {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Action failed.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Social Media Studio"
        subtitle="Compose, schedule and publish to Facebook Pages and Instagram Business."
        action={
          <div className="flex gap-2">
            <Link to="/admin/ai-social" className={btn}>
              AI &amp; Social Settings
            </Link>
            <button type="button" className={btnDark} onClick={() => navigate({ to: "/admin/social/$id", params: { id: "new" } })}>
              New Post
            </button>
          </div>
        }
      />

      <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        {POST_STATUSES.map((s) => (
          <div key={s} className="rounded-sm border border-black/10 bg-white px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-black/50">{s}</p>
            <p className="mt-1 text-2xl font-semibold text-black">{counts[s] ?? 0}</p>
          </div>
        ))}
      </div>

      {error && (
        <div className="rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      <div className={card}>
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <input
            className={`${input} flex-1 min-w-[220px]`}
            placeholder="Search title or caption…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select className={input} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {POST_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button type="button" className={btn} onClick={() => void refresh()}>
            Refresh
          </button>
        </div>

        {loading ? (
          <p className="py-10 text-center text-sm text-black/50">Loading posts…</p>
        ) : filtered.length === 0 ? (
          <p className="py-10 text-center text-sm text-black/50">No posts yet. Create your first post.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] text-left text-sm">
              <thead>
                <tr className="border-b border-black/10 text-[10px] uppercase tracking-[0.2em] text-black/50">
                  <th className="py-2 pr-3">Post</th>
                  <th className="py-2 pr-3">Platforms</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Created by</th>
                  <th className="py-2 pr-3">Created</th>
                  <th className="py-2 pr-3">Scheduled</th>
                  <th className="py-2 pr-3">Published</th>
                  <th className="py-2 pr-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-black/5 align-top">
                    <td className="py-3 pr-3">
                      <Link
                        to="/admin/social/$id"
                        params={{ id: p.id }}
                        className="font-medium text-black hover:text-[#C6A15B]"
                      >
                        {p.title || "Untitled post"}
                      </Link>
                      <p className="mt-1 max-w-[280px] truncate text-xs text-black/50">{p.caption}</p>
                    </td>
                    <td className="py-3 pr-3 text-xs text-black/70">
                      {p.platforms.length === 0
                        ? "—"
                        : p.platforms.map((pl) => platformLabel(pl.platform)).join(", ")}
                    </td>
                    <td className="py-3 pr-3">
                      <StatusPill tone={tone(p.status)}>{p.status}</StatusPill>
                      {p.platforms.some((pl) => pl.error) && (
                        <p className="mt-1 max-w-[200px] text-[11px] text-red-700">
                          {p.platforms.find((pl) => pl.error)?.error}
                        </p>
                      )}
                    </td>
                    <td className="py-3 pr-3 text-xs text-black/60">{p.createdByEmail ?? "—"}</td>
                    <td className="py-3 pr-3 text-xs text-black/60">{when(p.createdAt)}</td>
                    <td className="py-3 pr-3 text-xs text-black/60">{when(p.scheduledAt)}</td>
                    <td className="py-3 pr-3 text-xs text-black/60">
                      {when(p.publishedAt)}
                      {p.publishedByEmail && <span className="block text-black/40">{p.publishedByEmail}</span>}
                    </td>
                    <td className="py-3 pr-3">
                      <div className="flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.16em]">
                        <Link to="/admin/social/$id" params={{ id: p.id }} className="text-black/70 hover:text-[#C6A15B]">
                          Edit
                        </Link>
                        <button
                          type="button"
                          disabled={busy === "dup" + p.id}
                          className="text-black/70 hover:text-[#C6A15B]"
                          onClick={() => void act("dup" + p.id, () => dupFn({ data: { id: p.id } }))}
                        >
                          Duplicate
                        </button>
                        {p.status === "scheduled" && (
                          <button
                            type="button"
                            disabled={busy === "cancel" + p.id}
                            className="text-black/70 hover:text-[#C6A15B]"
                            onClick={() => void act("cancel" + p.id, () => cancelFn({ data: { id: p.id } }))}
                          >
                            Cancel
                          </button>
                        )}
                        <button
                          type="button"
                          disabled={busy === "del" + p.id}
                          className="text-red-700 hover:text-red-900"
                          onClick={() => {
                            if (!confirm("Delete this post permanently?")) return;
                            void act("del" + p.id, () => delFn({ data: { id: p.id } }));
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}